//src/pages/NovoCliente.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../axiosConfig";
import {
  ArrowLeft,
  Building2,
  Save,
  UserRound,
} from "lucide-react";

import "./EditarCliente.css";

import { validarSenhaFrontend } from "../utils/senhaUtils";

function NovoCliente() {
  const navigate = useNavigate();

  const [cliente, setCliente] = useState({
    nome: "",
    email: "",
    senha: "",
    tipo_pessoa: "fisica",
    cpf_cnpj: "",
    cnpj_empresa: "",
    telefone: "",
    cidade: "",
    uf: "",
    status: "ativo",
    tipo: "cliente",
    cep: "",
    tipo_identificador_pj: "",
    identificador_conta_pj: "",
    responsavel_nome: "",
    responsavel_cpf: "",
  });

  const [empresaEncontrada, setEmpresaEncontrada] = useState(null);
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const cnpjLimpo = limparDocumento(cliente.cnpj_empresa);

    if (cnpjLimpo.length === 14) {
      axios
        .get(`/empresas/cnpj/${cnpjLimpo}`)
        .then((res) => {
          const empresa = res.data;
          setEmpresaEncontrada(empresa);
          setCliente((prev) => ({
            ...prev,
            cidade: empresa.cidade || "",
            uf: empresa.estado || "",
            cep: empresa.cep || "",
          }));
        })
        .catch(() => {
          setEmpresaEncontrada(null);
          setCliente((prev) => ({
            ...prev,
            cidade: "",
            uf: "",
            cep: "",
          }));
        });
    } else {
      setEmpresaEncontrada(null);
      setCliente((prev) => ({
        ...prev,
        cidade: "",
        uf: "",
        cep: "",
      }));
    }
  }, [cliente.cnpj_empresa]);

  const limparDocumento = (valor) => String(valor || "").replace(/\D/g, "");

  const formatarDocumento = (valor, tipoPessoa) => {
    const numeros = limparDocumento(valor);

    if (tipoPessoa === "fisica") {
      return numeros
        .replace(/^(\d{3})(\d)/, "$1.$2")
        .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
        .replace(/\.(\d{3})(\d)/, ".$1-$2")
        .slice(0, 14);
    }

    return numeros
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .slice(0, 18);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "tipo_pessoa") {
      setCliente((prev) => ({
        ...prev,
        tipo_pessoa: value,
        cpf_cnpj: "",
        tipo_identificador_pj: "",
        identificador_conta_pj: "",
        responsavel_nome: "",
        responsavel_cpf: "",
      }));
      return;
    }

    if (name === "cpf_cnpj") {
      setCliente((prev) => ({
        ...prev,
        cpf_cnpj: formatarDocumento(value, prev.tipo_pessoa),
      }));
      return;
    }

    if (name === "cnpj_empresa") {
      setCliente((prev) => ({
        ...prev,
        cnpj_empresa: formatarDocumento(value, "juridica"),
      }));
      return;
    }

    if (name === "responsavel_cpf") {
      setCliente((prev) => ({
        ...prev,
        responsavel_cpf: formatarDocumento(value, "fisica"),
      }));
      return;
    }

    setCliente((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validarDocumentoBasico = () => {
    const doc = limparDocumento(cliente.cpf_cnpj);
    if (cliente.tipo_pessoa === "fisica") return doc.length === 11;
    if (cliente.tipo_pessoa === "juridica") return doc.length === 14;
    return false;
  };

  const handleSalvar = async () => {
    setErro("");
    setMensagem("");

    if (!cliente.nome || !cliente.email || !cliente.telefone) {
      setErro("Preencha nome, e-mail e telefone.");
      return;
    }

    const validacaoSenha = validarSenhaFrontend(cliente.senha);

      if (!validacaoSenha.valida) {
        setErro(validacaoSenha.mensagem);
        return;
      }

    if (!validarDocumentoBasico()) {
      setErro(cliente.tipo_pessoa === "fisica" ? "CPF inválido." : "CNPJ inválido.");
      return;
    }

    if (!empresaEncontrada) {
      setErro("Informe um CNPJ de empresa participante válido.");
      return;
    }

    if (cliente.tipo_pessoa === "juridica") {
      if (!cliente.tipo_identificador_pj || !cliente.identificador_conta_pj) {
        setErro("Para conta PJ, informe o tipo e o identificador da conta.");
        return;
      }

      if (!cliente.responsavel_nome || limparDocumento(cliente.responsavel_cpf).length !== 11) {
        setErro("Informe o responsável e o CPF do responsável.");
        return;
      }
    }

    try {
      setSalvando(true);

      const payload = {
        nome: cliente.nome,
        email: cliente.email,
        senha: cliente.senha,
        tipo: "cliente",
        tipo_pessoa: cliente.tipo_pessoa,
        cpf_cnpj: limparDocumento(cliente.cpf_cnpj),
        cnpj_empresa: limparDocumento(cliente.cnpj_empresa),
        telefone: cliente.telefone,
        cidade: cliente.cidade,
        estado: cliente.uf.toUpperCase(),
        status: cliente.status,

        tipo_identificador_pj:
          cliente.tipo_pessoa === "juridica"
            ? cliente.tipo_identificador_pj
            : null,

        identificador_conta_pj:
          cliente.tipo_pessoa === "juridica"
            ? cliente.identificador_conta_pj
            : null,

        responsavel_nome:
          cliente.tipo_pessoa === "juridica"
            ? cliente.responsavel_nome
            : null,

        responsavel_cpf:
          cliente.tipo_pessoa === "juridica"
            ? limparDocumento(cliente.responsavel_cpf)
            : null,
      };

      await axios.post("/clientes", payload, {
        headers: { "x-interno": "true" },
      });

      setMensagem("Cliente cadastrado com sucesso!");
      setTimeout(() => navigate("/clientes"), 1200);
    } catch (err) {
      console.error("Erro ao salvar cliente:", err?.response?.data || err);
      setErro(err.response?.data?.error || "Erro ao salvar cliente.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="editarCliente-page">
      <div className="editarCliente-topbar">
        <div>
          <button
            type="button"
            className="editarCliente-back asButton"
            onClick={() => navigate("/clientes")}
          >
            <ArrowLeft size={15} />
            Voltar para clientes
          </button>

          <h2>Novo Cliente</h2>
          <p>
            Cadastre uma nova conta de cliente com vínculo à empresa participante.
          </p>
        </div>

        <button
          type="button"
          className="editarCliente-btn primary"
          onClick={handleSalvar}
          disabled={salvando}
        >
          <Save size={16} />
          {salvando ? "Salvando..." : "Salvar cliente"}
        </button>
      </div>

      {erro && <div className="editarCliente-alert erro">{erro}</div>}
      {mensagem && <div className="editarCliente-alert sucesso">{mensagem}</div>}

      <div className="editarCliente-summary">
        <div className="editarCliente-identity">
          <div className="editarCliente-avatar">
            {cliente.nome?.charAt(0)?.toUpperCase() || "C"}
          </div>

          <div>
            <h3>{cliente.nome || "Novo cadastro"}</h3>
            <span>{cliente.email || "E-mail ainda não informado"}</span>

            <div className="editarCliente-badgeRow">
              <span
                className={`editarCliente-badge ${
                  cliente.tipo_pessoa === "juridica" ? "pj" : "pf"
                }`}
              >
                {cliente.tipo_pessoa === "juridica" ? (
                  <Building2 size={13} />
                ) : (
                  <UserRound size={13} />
                )}
                {cliente.tipo_pessoa === "juridica" ? "Pessoa Jurídica" : "Pessoa Física"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="editarCliente-grid">
        <section className="editarCliente-card">
          <div className="editarCliente-cardHeader">
            <h3>Dados principais</h3>
          </div>

          <div className="editarCliente-formGrid">
            <div className="editarCliente-field">
              <label>Nome / Razão social</label>
              <input name="nome" value={cliente.nome} onChange={handleChange} />
            </div>

            <div className="editarCliente-field">
              <label>E-mail</label>
              <input
                type="email"
                name="email"
                value={cliente.email}
                onChange={handleChange}
              />
            </div>

            <div className="editarCliente-field">
              <label>Telefone</label>
              <input
                name="telefone"
                value={cliente.telefone}
                onChange={handleChange}
              />
            </div>

            <div className="editarCliente-field">
              <label>Tipo de pessoa</label>
              <select
                name="tipo_pessoa"
                value={cliente.tipo_pessoa}
                onChange={handleChange}
              >
                <option value="fisica">Pessoa Física / colaborador</option>
                <option value="juridica">Pessoa Jurídica / empresa</option>
              </select>
            </div>

            <div className="editarCliente-field">
              <label>{cliente.tipo_pessoa === "fisica" ? "CPF" : "CNPJ"}</label>
              <input
                name="cpf_cnpj"
                value={cliente.cpf_cnpj}
                onChange={handleChange}
              />
            </div>

            <div className="editarCliente-field">
              <label>Senha inicial</label>
              <input
                type="password"
                name="senha"
                value={cliente.senha}
                onChange={handleChange}
                placeholder="Mínimo 8 caracteres, com letras e números"
              />
            </div>

            {cliente.tipo_pessoa === "juridica" && (
              <>
                <div className="editarCliente-field">
                  <label>Esta conta representa</label>
                  <select
                    name="tipo_identificador_pj"
                    value={cliente.tipo_identificador_pj}
                    onChange={handleChange}
                  >
                    <option value="">Selecione</option>
                    <option value="empresa">A empresa como um todo</option>
                    <option value="departamento">Um setor/departamento</option>
                    <option value="colaborador">Um colaborador específico</option>
                  </select>
                </div>

                <div className="editarCliente-field">
                  <label>
                    {cliente.tipo_identificador_pj === "departamento"
                      ? "Nome do setor/departamento"
                      : cliente.tipo_identificador_pj === "colaborador"
                      ? "Nome do colaborador"
                      : "Identificador da conta"}
                  </label>
                  <input
                    name="identificador_conta_pj"
                    value={cliente.identificador_conta_pj}
                    onChange={handleChange}
                  />
                </div>

                <div className="editarCliente-field">
                  <label>Responsável</label>
                  <input
                    name="responsavel_nome"
                    value={cliente.responsavel_nome}
                    onChange={handleChange}
                  />
                </div>

                <div className="editarCliente-field">
                  <label>CPF do responsável</label>
                  <input
                    name="responsavel_cpf"
                    value={cliente.responsavel_cpf}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}
          </div>
        </section>

        <section className="editarCliente-card">
          <div className="editarCliente-cardHeader">
            <h3>Empresa vinculada</h3>
          </div>

          <div className="editarCliente-formGrid">
            <div className="editarCliente-field">
              <label>CNPJ da empresa participante</label>
              <input
                name="cnpj_empresa"
                value={cliente.cnpj_empresa}
                onChange={handleChange}
              />
              <small>
                A empresa precisa estar cadastrada e ativa no programa.
              </small>
            </div>

            <div className="editarCliente-field">
              <label>Razão social</label>
              <input
                value={empresaEncontrada?.razao_social || "—"}
                disabled
              />
            </div>

            <div className="editarCliente-field">
              <label>Cidade</label>
              <input value={cliente.cidade || "—"} disabled />
            </div>

            <div className="editarCliente-field">
              <label>UF</label>
              <input value={cliente.uf || "—"} disabled />
            </div>

            <div className="editarCliente-field">
              <label>CEP</label>
              <input value={cliente.cep || "—"} disabled />
            </div>

            <div className="editarCliente-field">
              <label>Status</label>
              <select name="status" value={cliente.status} onChange={handleChange}>
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
              </select>
            </div>
          </div>
        </section>
      </div>

      <div className="editarCliente-footerActions">
        <button
          type="button"
          className="editarCliente-btn secondary"
          onClick={() => navigate("/clientes")}
        >
          Cancelar
        </button>

        <button
          type="button"
          className="editarCliente-btn primary"
          onClick={handleSalvar}
          disabled={salvando}
        >
          <Save size={16} />
          {salvando ? "Salvando..." : "Cadastrar cliente"}
        </button>
      </div>
    </div>
  );
}

export default NovoCliente;